import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFirePerformance } from '@angular/fire/compat/performance'; // Updated import
import { Router } from '@angular/router';
import { CommonService, IdleService, LocalStorageService, NotificationService } from '@app/core';
import { environment } from '@environments/environment';
import { throwError } from 'rxjs';
import { catchError, finalize, tap, timeout } from 'rxjs/operators';
import { ERROR_STRINGS, GLOBAL_ERROR_MESSAGES, StatusCode, MAX_TIMEOUT, REQ_TIMEOUT, AnalyticLogLabels, APIRequestLabels, VALIDATIONS } from '@app/shared';
import { AnalyticService } from '../analytic.service';

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {

    reqProgressExpectionals: string[] = [APIRequestLabels.sendBroadcast, APIRequestLabels.sendBroadcastPreview, APIRequestLabels.sendWelcomeMessage, 
        APIRequestLabels.getBroadcastHistoryRefresh, APIRequestLabels.sendSurvey, APIRequestLabels.sendSurveyHistoryRerun, APIRequestLabels.getUserDownloadLink, APIRequestLabels.getWorkflowAnalyticsDownloadLink];
    reqTimeoutExpectionals: string[] = [APIRequestLabels.sendBroadcast, APIRequestLabels.sendWelcomeMessage, APIRequestLabels.sendSurvey, APIRequestLabels.sendSurveyHistoryRerun, APIRequestLabels.getOrganizationsAsync];
    maxTimeoutEndPoints: string[] = ['bulkupload', 'broadcast', 'welcomeMessage', 'survey/','surveyhistory', 'rerun', '/users/download', '/interests/organization', '/interests/association', '/workflowanalytics/download'];
    
    applyAuthTokenURLs = ['auth/getCurrentUser', 'auth/createUser', 'auth/resendActivationLink', 'auth/acceptDocuments', 'auth/mfa', 'auth/intercom-token'];
    noAuthExceptionEndpoints = ['service-status']; // note: use only for common services endpoints
    
    constructor(
        private readonly _router: Router,
        private readonly _localS: LocalStorageService,
        private readonly _commonS: CommonService,
        private readonly _analytics: AnalyticService,
        private readonly _performance: AngularFirePerformance,
        private readonly _notificationService: NotificationService,
        private readonly _idleService: IdleService
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler) {
        const { url } = req;

        if (!window.navigator.onLine) {
            // check if the user is in online
            this._commonS.setApplicationStatus({ status: 'Error', message: GLOBAL_ERROR_MESSAGES.NO_INTERNET });
            return next.handle(req).pipe(finalize(() => this._commonS.setLoading(false)));
        } else {
            let reqHeaders = req.headers;
            reqHeaders = reqHeaders.set('ngsw-bypass', 'true');
            reqHeaders = reqHeaders.set('Access-Control-Allow-Origin', location?.origin || '');
            
            const byPassedRequest = req.clone({ headers: reqHeaders });
            const traceURL = url.replace(environment.apiBaseUrl, '')
                .split('/')
                .filter(element => !(/\d/.test(element)))
                .join('/');
            let trace = this._performance.trace(traceURL);
            trace.then((t:any) => t.start());

            if (!this.hasProgressExceptional(byPassedRequest)) {
                this._commonS.setLoading(true);
            }

            if (url.includes('auth') && (!this.applyAuthTokenURLs.some(exceptionalURL => url.includes(exceptionalURL)))) {
                let reqStartTime = performance.now();
                return next.handle(byPassedRequest).pipe(
                    timeout(REQ_TIMEOUT),
                    tap(
                        event => { },
                        error => {
                            if (error instanceof HttpErrorResponse) {
                                this.logServerErrorInGA(byPassedRequest, error, reqStartTime);
                            }
                        }
                    ),
                    finalize(() => {
                        if (!this.hasProgressExceptional(byPassedRequest)) {
                            this._commonS.setLoading(false);
                        }
                        trace.then((t:any) => t.stop());
                    })
                );
            } else {
                return this.apiCallWithValidToken(byPassedRequest, next).pipe(
                    finalize(() => {
                        if (!this.hasProgressExceptional(byPassedRequest)) {
                            this._commonS.setLoading(false);
                        }
                        trace.then((t:any) => t.stop());
                    })
                );
            }
        }
    }

    apiCallWithValidToken(req: HttpRequest<any>, next: HttpHandler) {
        // Get the auth token from the service.
        const token: string = this._localS.getAuthData()?.accessToken ?? '';
        let { url } = req;
        const reqTimeout = this.maxTimeoutEndPoints.some(endpoint => url.includes(endpoint)) ? MAX_TIMEOUT : REQ_TIMEOUT;

        // Clone the request and replace the original headers with
        // cloned headers, updated with the authorization.
        let reqHeaders = req.headers;
        reqHeaders = reqHeaders.set('Authorization', `Bearer ${token}`);

        //Add OrgID as custom header; this is for 1115 multi-tenancy
        if (this._localS.getAuthData()?.orgId) {
            const orgIdHeaderValue: string = this._localS.getAuthData()?.orgId?.toString() ?? '';
            reqHeaders = reqHeaders.set('BloomingHealth-Org-ID', orgIdHeaderValue);
        }

        const authReq = req.clone({ headers: reqHeaders });
        
        let reqStartTime = performance.now();
        // send cloned request with header to the next handler.
        return next.handle(authReq).pipe(
            timeout(reqTimeout),
            catchError(error => {
                // When request of type Blob, the error is also in Blob instead of object of the json data
                this._commonS.setNavLoading(false);
                if (error instanceof HttpErrorResponse && error.error instanceof Blob) {
                    return new Promise<any>((resolve, reject) => {
                        let reader = new FileReader();
                        reader.onload = (e: Event) => {
                            try {
                                const errmsg = JSON.parse((<any>e.target).result);
                                reject(new Error(errmsg));
                            } catch (e) {
                                reject(new Error(error?.error || ''));
                            }
                        };
                        reader.onerror = (e) => {
                            reject(new Error(error?.error || ''));
                        };
                        reader.readAsText(error.error);
                    });
                }
                if(error.error?.error?.includes(VALIDATIONS.TOKEN.EXPIRED)) {
                    this.handleErrorAndLogout(error);

                }
                return throwError(error);
            }),
            tap(
                event => { },
                error => {
                    this._commonS.setNavLoading(false);
                    if (error.name && (error.name === ERROR_STRINGS.TIMEOUT) && !this.reqTimeoutExpectionals.some(reqName => (req.headers.get('method-name') == reqName))) {
                        this._commonS.setScreenStatus({ status: 'Error', message: GLOBAL_ERROR_MESSAGES.REQUEST_TIMEOUT });
                    }
                    if (error instanceof HttpErrorResponse) {
                        if (error.status === StatusCode.Unauthorized && !this.noAuthExceptionEndpoints.find(endpoint => req.url.includes(endpoint))) {
                            this.handleErrorAndLogout(error);
                        }
                        this.logServerErrorInGA(req, error, reqStartTime);
                    }
                }
            )
        );
    }

    logServerErrorInGA(request: HttpRequest<any>, error: HttpErrorResponse, reqStartTime: number) {
        let paramsValue: { [key:string]: any } = {}; 

        if (request.params.toString()?.length) {
            request.params.keys().forEach(pKey => 
                (paramsValue[pKey] = request.params.get(pKey)));
        }
        this._analytics.logAnalyticEvent(
            AnalyticLogLabels.ServerError,
            {
                'api_url': request.url.replace(environment.apiBaseUrl, ''),
                'api_name': request.headers.get('method-name') ?? '(not set)',
                'api_query_parameter': JSON.stringify(paramsValue),
                'api_request_method': request.method,
                'api_response_type': request.responseType.toString(),
                'api_status_code': error.status.toString(),
                'api_error_message': JSON.stringify(error.error),
                'api_resonse_datetime': new Date().toString(),
                'api_response_time': (performance.now() - reqStartTime).toString()
            })
    }

    hasProgressExceptional(req: HttpRequest<any>) {
        return this.reqProgressExpectionals.some(reqName => (req.headers.get('method-name') == reqName))
    }

    private handleErrorAndLogout(error: any) {
        this._localS.clearStorage();
        this._idleService.stopWatching();
  
        this._router.navigate(['auth/signin']).then(() => {
          this._notificationService.showError(error);
        });
      }
}
